import React from 'react';

export interface MonthlyTrendData {
  month: string;
  revenue: number;
  expenses: number;
  clients: number;
  tasks: number;
}

export interface MonthlyTrendsProps {
  data: MonthlyTrendData[];
  isLoading?: boolean;
  height?: number;
}

const MonthlyTrends: React.FC<MonthlyTrendsProps> = ({ data, isLoading, height = 300 }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No trend data available
        </div>
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map(d => d.revenue), 0);
  const maxExpenses = Math.max(...data.map(d => d.expenses), 0);
  const maxClients = Math.max(...data.map(d => d.clients), 0);
  const maxTasks = Math.max(...data.map(d => d.tasks), 0);

  const maxValue = Math.max(maxRevenue, maxExpenses, maxClients * 100, maxTasks * 10);
  const chartHeight = height - 100;
  const chartWidth = 1000;
  const padding = 40;

  const lines = [
    { key: 'revenue', label: 'Revenue', color: '#3b82f6', format: (v: number) => `€${(v / 1000).toFixed(1)}k` },
    { key: 'expenses', label: 'Expenses', color: '#ef4444', format: (v: number) => `€${(v / 1000).toFixed(1)}k` },
    { key: 'clients', label: 'Clients', color: '#10b981', format: (v: number) => `${v}`, scale: 100 },
    { key: 'tasks', label: 'Tasks', color: '#f59e0b', format: (v: number) => `${v}`, scale: 10 },
  ];

  const getPoints = (key: string, scale: number = 1) => {
    return data.map((item, index) => {
      const value = (item as any)[key] * scale;
      const x = (index * (chartWidth / data.length)) + (chartWidth / data.length / 2);
      const y = chartHeight - (value / maxValue) * chartHeight;
      return { x, y, value: (item as any)[key], month: item.month };
    });
  };

  const getPathData = (points: Array<{ x: number; y: number }>) => {
    return points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Monthly Trends</h2>
        <div className="flex items-center gap-3 flex-wrap">
          {lines.map((line) => (
            <div key={line.key} className="flex items-center gap-2">
              <div className="w-3 h-0.5" style={{ backgroundColor: line.color }}></div>
              <span className="text-xs text-gray-600">{line.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="relative" style={{ height: `${height}px` }}>
        <svg width="100%" height={height} viewBox={`0 0 ${chartWidth} ${height}`} className="overflow-visible">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (chartHeight * ratio);
            const value = maxValue - (maxValue * ratio);
            return (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-500"
                >
                  {value >= 1000 ? `€${(value / 1000).toFixed(1)}k` : value.toFixed(0)}
                </text>
              </g>
            );
          })}

          {/* Lines */}
          {lines.map((line) => {
            const points = getPoints(line.key, line.scale);
            const pathData = getPathData(points);
            return (
              <g key={line.key}>
                <path
                  d={pathData}
                  fill="none"
                  stroke={line.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {points.map((point, index) => (
                  <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill={line.color}
                    className="hover:r-6 transition-all cursor-pointer"
                  >
                    <title>
                      {point.month} - {line.label}: {line.format(point.value)}
                    </title>
                  </circle>
                ))}
              </g>
            );
          })}

          {/* X-axis labels */}
          {data.map((item, index) => {
            const x = (index * (chartWidth / data.length)) + (chartWidth / data.length / 2);
            return (
              <text
                key={index}
                x={x}
                y={chartHeight + padding - 10}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {item.month}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default MonthlyTrends;

